import  { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { Form, TextArea, Checkbox } from 'semantic-ui-react';
import { FormSchemaAwareWidget } from '~/components/form';
import Button from '~/components/Button';
import Modal from '~/components/Modal';
import { usePortfolioContext } from '~/hooks';
import { useForm } from '~/hooks/providers/FormContextProvider';
import { getPriorInvestors } from '~/api/team';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { PriorInvestor } from '~/types/company';
import { useSubmitPortfolio } from '~/mutations/portfolio/useSubmitPortfolio';


interface SubmitPortfolioModalProps {
  closeModal: () => void;
  handleSubmit: () => void;
}

interface PortfolioSubmissionFormState {
  preferredInvestors: PriorInvestor[];
  preferredInvestorDetails: string;
  autoGrantDocAccess: boolean;
}

const SubmitPortfolioModal = ({
  closeModal,
  handleSubmit
}: SubmitPortfolioModalProps) => {
  const { portfolio } = usePortfolioContext();
  const { formState } = useForm<PortfolioSubmissionFormState>();
  const { permissions } = usePermissions();
  const [priorInvestors, setPriorInvestors] = useState([]);

  const { status: submitPortfolioStatus } = useSubmitPortfolio();
  const { mutateAsync: fetchPriorInvestors, status: fetchPriorInvestorsStatus } = useMutation(async () => await getPriorInvestors(portfolio?.installer?.id));

  const handlePriorInvestors = async () => {
    try {
      if (!portfolio?.installer?.id) {
        return [];
      }
      const investors = await fetchPriorInvestors(portfolio?.installer?.id) as any;
      const options = investors.map((investor: any) => ({key: investor.id, value: investor, text: investor.name}));
      setPriorInvestors(options);
    } catch (error) {
      toast.error('Error submitting preferred investors.');
    }
  };
  
  useEffect(() => {
    handlePriorInvestors();
  }, [portfolio?.installer?.id]);

  const hasErrors = !permissions.isDeveloper || formState.preferredInvestors.length > 5 || submitPortfolioStatus === 'loading' || fetchPriorInvestorsStatus === 'loading';

  return (
    <>
      <Modal.Title>
        Confirm portfolio submission
      </Modal.Title>
      <Modal.Content>
        <p>Conductor&apos;s algorithm will match you with the best investor options for your portfolio. If you&apos;d like to ensure certain investors do (or don&apos;t) see your portfolio, indicate that below.</p>
        <Form>
          <FormSchemaAwareWidget
            schemaKey="preferredInvestors"
            placeholder="Previously matched investors"
            multiple
            options={priorInvestors}
            maxSelections={5}
          />  
          <FormSchemaAwareWidget as={TextArea}
            schemaKey="preferredInvestorDetails"
            placeholder="Use this space to provide any additional context, including other preferred investors who are not on the above list or investors with whom you do not want to be matched."
          />
         <FormSchemaAwareWidget as={Checkbox}
            schemaKey="autoGrantDocAccess"
            label='Share my uploaded docs with investors'
            fieldLabelStyle={{fontSize: '0.875rem'}}
          />
        </Form>
      </Modal.Content>
      <Modal.Footer>
        <Button
          primary
          outlined
          size="medium"
          onClick={() => closeModal()}
        >
          Cancel
        </Button>
        <Button 
          primary  
          onClick={() => handleSubmit()}
          loading={submitPortfolioStatus === 'loading'}
          disabled={hasErrors}
        >
          Submit portfolio 
        </Button>
      </Modal.Footer>
    </>
  );
};

export default SubmitPortfolioModal;